import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Pointer } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ColorMoment } from "@/hooks/useColorMoments";
import { ColorPaletteDisplay } from "@/components/ColorPaletteDisplay";

/**
 * Convert hex color to HSL
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Compute today's preferred hue based on the calendar date
 */
function getTodaysPreferredHue(): number {
  const now = new Date();
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) /
      (1000 * 60 * 60 * 24),
  );
  const goldenRatio = 0.618033988749895;
  const hue = ((dayOfYear * goldenRatio) % 1) * 360;
  return Math.floor(hue);
}

/**
 * Check if a color matches a hue range
 */
function colorMatchesHue(
  hex: string,
  targetHue: number,
  tolerance = 35,
): boolean {
  const { h, s, l } = hexToHsl(hex);
  if (s < 15) return false;
  if (l < 15 || l > 90) return false;
  const diff = Math.abs(h - targetHue);
  return diff <= tolerance || diff >= 360 - tolerance;
}

/**
 * Get hue category name for a color
 */
function getHueCategory(hex: string): string {
  const { h, s, l } = hexToHsl(hex);
  if (s < 15) return "gray";
  if (l < 15) return "dark";
  if (l > 90) return "light";
  if (h < 15 || h >= 345) return "red";
  if (h < 45) return "orange";
  if (h < 75) return "yellow";
  if (h < 150) return "green";
  if (h < 195) return "teal";
  if (h < 255) return "blue";
  if (h < 285) return "purple";
  if (h < 345) return "pink";
  return "red";
}

function randomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Get contrasting text color (black or white) for a background color
 */
function getContrastColor(hex: string | undefined | null): string {
  if (!hex) return "#1c1917";
  const { l } = hexToHsl(hex);
  return l > 55 ? "#1c1917" : "#fafaf9"; // stone-900 or stone-50
}

// Color remarks
// Statements that follow the color name (format: "<ColorName>... <statement>")
const COLOR_STATEMENTS: Record<
  string,
  { neutral: string[]; positive: string[] }
> = {
  red: {
    neutral: ["Bold, I suppose.", "Passionate. Or angry."],
    positive: ["Yes. This is what I wanted.", "It speaks to me."],
  },
  orange: {
    neutral: ["Neither red nor yellow.", "Energetic."],
    positive: ["Exactly what I craved.", "Amber warmth. Perfect."],
  },
  yellow: {
    neutral: ["Cheerful. Aggressively so.", "Demanding attention."],
    positive: ["The sun itself approves.", "Golden brilliance."],
  },
  green: {
    neutral: ["Nature's default.", "How... expected."],
    positive: ["Ideal for today.", "Exceptional."],
  },
  teal: {
    neutral: ["Couldn't commit.", "Refreshing, I suppose."],
    positive: ["Venus smiles upon this.", "Delights me."],
  },
  blue: {
    neutral: ["Like sadness. Or the sky.", "Classic."],
    positive: ["This is the one I sought.", "Azure perfection."],
  },
  purple: {
    neutral: ["Royal pretensions.", "Dramatic."],
    positive: ["Jupiter's own hue.", "Transcendent."],
  },
  pink: {
    neutral: ["Red that couldn't commit.", "Romantic."],
    positive: ["Unexpectedly right.", "Works beautifully."],
  },
  gray: {
    neutral: ["Absence of commitment.", "How... safe."],
    positive: ["Restrained elegance.", "Has purpose."],
  },
  dark: {
    neutral: ["Brooding.", "Moody."],
    positive: ["Bold.", "Excellence."],
  },
  light: {
    neutral: ["Almost not there.", "Ethereal."],
    positive: ["Delicate.", "Perfection."],
  },
};

const RATINGS = [
  { threshold: 0, label: "Disappointing", sublabel: "Try again." },
  { threshold: 1, label: "Passable", sublabel: "Barely." },
  { threshold: 2, label: "Acceptable", sublabel: "I've seen worse." },
  { threshold: 3, label: "Commendable", sublabel: "You have potential." },
  { threshold: 4, label: "Impressive", sublabel: "I'm almost moved." },
  { threshold: 5, label: "Magnificent", sublabel: "The cosmos approves." },
  { threshold: 6, label: "Transcendent", sublabel: "I have no notes." },
];

const GREETINGS = [
  "Ah, you're here...",
  "Mm. You've returned...",
  "Oh. It's you again...",
  "Very well...",
  "You summoned me...",
];

const PROMPTS = [
  "Show me what you've captured.",
  "Present your colors.",
  "What have you brought me?",
];

const CLOSINGS = [
  "Farewell.",
  "Until next time.",
  "We're done here.",
  "Goodbye for now.",
];

interface ColorRemark {
  colorName: string;
  statement: string;
  color: string;
}

interface ReviewContent {
  greeting: string;
  prompt: string;
  remarks: ColorRemark[];
  ratingLabel: string;
  ratingSublabel: string;
  closing: string;
  colors: string[];
}

interface ColorCriticProps {
  latestMoment?: ColorMoment;
  className?: string;
  /** Colors from a just-submitted moment - triggers the critic to appear */
  justSubmittedColors?: string[];
  /** Callback when the critic dismisses after reviewing a submission */
  onDismiss?: () => void;
}

type CriticState =
  | "hidden"
  | "peeking"
  | "phase1"
  | "phase2"
  | "phase3"
  | "closing";
type DisplayPhase = "phase1" | "phase2" | "phase3" | null;

// Preload critic images on module load
const preloadImages = ["/shakespeare-critic.png", "/shakespeare-thought-2.png"];
preloadImages.forEach((src) => {
  const img = new Image();
  img.src = src;
});

export function ColorCritic({
  latestMoment,
  className,
  justSubmittedColors,
  onDismiss,
}: ColorCriticProps) {
  const [state, setState] = useState<CriticState>("hidden");
  const [content, setContent] = useState<ReviewContent | null>(null);
  const [visibleLines, setVisibleLines] = useState(0);
  // Track which phase we're actually displaying content for (prevents flash of new content)
  const [displayPhase, setDisplayPhase] = useState<DisplayPhase>(null);
  // Track if we've ever been shown (to avoid initial mount animation)
  const [hasEverShown, setHasEverShown] = useState(false);

  const todaysHue = useMemo(() => getTodaysPreferredHue(), []);

  // Track the colors we're reviewing (from just-submitted or latest moment)
  const [reviewColors, setReviewColors] = useState<string[]>([]);

  // Track which colors we've already rolled for
  const rolledForColorsRef = useRef<string[] | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When justSubmittedColors is present and we're hidden, trigger the critic to appear.
  // Probability gating is handled externally by usePostSubmissionEvent.
  useEffect(() => {
    if (
      justSubmittedColors &&
      justSubmittedColors.length > 0 &&
      state === "hidden"
    ) {
      // Skip if we already triggered for these exact colors
      if (rolledForColorsRef.current === justSubmittedColors) return;
      rolledForColorsRef.current = justSubmittedColors;

      setReviewColors(justSubmittedColors);
      // Small delay after confetti starts, then show the critic
      // Use ref to avoid cleanup clearing it
      timerRef.current = setTimeout(() => {
        // First render in hidden state, then animate to peeking
        setHasEverShown(true);
        // Use requestAnimationFrame to ensure hidden state renders first
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setState("peeking");
          });
        });
      }, 1500);
    }
  }, [justSubmittedColors, state, onDismiss]);

  // Cleanup timer on unmount only
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const generateContent = useCallback((): ReviewContent => {
    const greeting = randomFrom(GREETINGS);
    const prompt = randomFrom(PROMPTS);
    const closing = randomFrom(CLOSINGS);

    // Use reviewColors (from just-submitted) if available, otherwise fall back to latestMoment
    const colorsToUse =
      reviewColors.length > 0 ? reviewColors : latestMoment?.colors || [];

    if (!colorsToUse.length) {
      return {
        greeting,
        prompt,
        remarks: [
          {
            colorName: "Nothing",
            statement: "You've shared nothing.",
            color: "#9ca3af",
          },
          {
            colorName: "The void",
            statement: "stares back.",
            color: "#374151",
          },
        ],
        ratingLabel: "Nonexistent",
        ratingSublabel: "Share something first.",
        closing,
        colors: [],
      };
    }

    // Pick colors ensuring no duplicate color families
    const shuffledColors = [...colorsToUse].sort(() => Math.random() - 0.5);
    const colorsToReview: string[] = [];
    const usedCategories = new Set<string>();

    for (const color of shuffledColors) {
      if (colorsToReview.length >= 3) break;
      const category = getHueCategory(color);
      if (!usedCategories.has(category)) {
        colorsToReview.push(color);
        usedCategories.add(category);
      }
    }

    let positiveCount = 0;

    const remarks = colorsToReview.map((color) => {
      const category = getHueCategory(color);
      const isPositive = colorMatchesHue(color, todaysHue);
      if (isPositive) positiveCount++;
      const pool =
        COLOR_STATEMENTS[category]?.[isPositive ? "positive" : "neutral"] ||
        COLOR_STATEMENTS.gray.neutral;
      // Use category name with first letter capitalized (e.g., "Red", "Blue", "Teal")
      const categoryName = category.charAt(0).toUpperCase() + category.slice(1);
      return { colorName: categoryName, statement: randomFrom(pool), color };
    });

    const baseScore = Math.floor(Math.random() * 2) + 1;
    const totalScore = Math.min(baseScore + positiveCount * 2, 6);
    const rating =
      RATINGS.find((r) => r.threshold >= totalScore) ||
      RATINGS[RATINGS.length - 1];

    return {
      greeting,
      prompt,
      remarks,
      ratingLabel: rating.label,
      ratingSublabel: rating.sublabel,
      closing,
      colors: colorsToUse,
    };
  }, [latestMoment, todaysHue, reviewColors]);

  // Line type for phase content
  type PhaseLine = string | { type: "remark"; remark: ColorRemark };

  // Get lines for the display phase (not the state, to prevent flash)
  const getPhaseLines = useCallback(
    (phase: DisplayPhase): PhaseLine[] => {
      if (!content || !phase) return [];
      if (phase === "phase1") {
        return [content.greeting, content.prompt];
      }
      if (phase === "phase2") {
        return [
          "__PALETTE__",
          ...content.remarks.map((r) => ({
            type: "remark" as const,
            remark: r,
          })),
        ];
      }
      if (phase === "phase3") {
        return [
          `Overall: ${content.ratingLabel}`,
          content.ratingSublabel,
          content.closing,
        ];
      }
      return [];
    },
    [content],
  );

  const phaseLines = getPhaseLines(displayPhase);

  // Sync displayPhase with state, but with a slight delay to prevent flash
  useEffect(() => {
    if (state === "phase1" || state === "phase2" || state === "phase3") {
      // Small delay before showing new phase content
      const timer = setTimeout(() => {
        setDisplayPhase(state);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setDisplayPhase(null);
    }
  }, [state]);

  // Fade in lines one by one
  useEffect(() => {
    if (!displayPhase) {
      setVisibleLines(0);
      return;
    }

    if (visibleLines < phaseLines.length) {
      const timer = setTimeout(
        () => {
          setVisibleLines((v) => v + 1);
        },
        visibleLines === 0 ? 800 : 1200,
      );
      return () => clearTimeout(timer);
    }
  }, [displayPhase, visibleLines, phaseLines.length]);

  const handleShakespeareClick = useCallback(() => {
    if (state === "peeking") {
      setContent(generateContent());
      setVisibleLines(0);
      setState("phase1");
    }
  }, [state, generateContent]);

  const handleBubbleClick = useCallback(() => {
    if (state === "phase1") {
      setVisibleLines(0);
      setDisplayPhase(null); // Clear display immediately to prevent flash
      setState("phase2");
    } else if (state === "phase2") {
      setVisibleLines(0);
      setDisplayPhase(null); // Clear display immediately to prevent flash
      setState("phase3");
    } else if (state === "phase3") {
      setState("closing");
      setTimeout(() => {
        setState("hidden");
        setReviewColors([]);
        onDismiss?.();
      }, 600);
    }
  }, [state, onDismiss]);

  const handleBackdropClick = useCallback(() => {
    if (state === "phase1" || state === "phase2" || state === "phase3") {
      setState("closing");
      setTimeout(() => {
        setState("hidden");
        setReviewColors([]);
        onDismiss?.();
      }, 600);
    }
  }, [state, onDismiss]);

  const isReviewing =
    state === "phase1" || state === "phase2" || state === "phase3";

  return (
    <>
      {/* Animations */}
      <style>{`
        @keyframes letterJitter {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(0.3px, -0.3px); }
          50% { transform: translate(-0.3px, 0.2px); }
          75% { transform: translate(0.2px, 0.3px); }
        }
        .jitter-letter {
          animation: letterJitter 2.5s ease-in-out infinite;
          display: inline-block;
        }
        @keyframes pointerPulse {
          0%, 100% { opacity: 0.25; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.1); }
        }
        .pointer-hint {
          animation: pointerPulse 1.5s ease-in-out infinite;
        }
      `}</style>

      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/70 transition-opacity duration-500 z-[60]",
          isReviewing ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={handleBackdropClick}
      />

      {/* Shakespeare - only rendered after first trigger to avoid mount animation */}
      {hasEverShown && (
        <div
          className={cn(
            "fixed bottom-0 left-1/2 -translate-x-1/2 z-[80] select-none transition-all duration-700 ease-out",
            state === "hidden" || state === "closing"
              ? "pointer-events-none"
              : "cursor-pointer",
            className,
          )}
          style={{
            width: isReviewing ? "100%" : undefined,
            maxWidth: isReviewing ? "32rem" : undefined,
          }}
          onClick={
            state === "hidden" || state === "closing"
              ? undefined
              : isReviewing
                ? handleBubbleClick
                : handleShakespeareClick
          }
        >
          <div
            className={cn(
              "transition-all duration-700 ease-out flex justify-center",
              state === "hidden" && "translate-y-full opacity-0",
              state === "peeking" && "translate-y-[70%]",
              state === "closing" && "translate-y-full opacity-0",
              isReviewing && "translate-y-[45%]",
            )}
          >
            <img
              src="/shakespeare-critic.png"
              alt="The Color Critic"
              className={cn(
                "h-auto drop-shadow-2xl transition-all duration-700 ease-out",
                isReviewing
                  ? "w-full object-cover object-top"
                  : "w-72 sm:w-80 md:w-96 hover:scale-105",
              )}
              draggable={false}
            />
          </div>
        </div>
      )}

      {/* Thought bubble - above Shakespeare */}
      {isReviewing && (
        <div className="fixed inset-x-0 bottom-[25dvh] md:bottom-[24vh] z-[85] flex justify-center pointer-events-none overflow-visible">
          <div
            className="pointer-events-auto cursor-pointer w-[180vw] max-w-none md:w-full md:max-w-4xl transition-all duration-500 scale-125 md:scale-100"
            onClick={handleBubbleClick}
          >
            <div className="relative">
              {/* Thought bubble image */}
              <div className="relative w-[130dvw] pb-[85%] md:w-full">
                <img
                  src="/shakespeare-thought-2.png"
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain"
                  draggable={false}
                />
              </div>

              {/* Content overlay - positioned inside the cloud */}
              <div className="absolute inset-0 flex flex-col items-center justify-center px-[15%] pt-[8%] pb-[25%]">
                <div className="w-full h-full flex flex-col items-center justify-center text-center">
                  {phaseLines.map((line, index) => {
                    if (line === "__PALETTE__") {
                      return (
                        <div
                          key={index}
                          className={cn(
                            "mb-[2vw] transition-opacity duration-1000",
                            visibleLines > index ? "opacity-100" : "opacity-0",
                          )}
                        >
                          {content && content.colors.length > 0 && (
                            <ColorPaletteDisplay
                              colors={content.colors}
                              layout={latestMoment?.layout || "horizontal"}
                              className="w-[15vw] h-[15vw] max-w-24 max-h-24 min-w-16 min-h-16 rounded-xl overflow-hidden shadow-lg"
                            />
                          )}
                        </div>
                      );
                    }

                    // Handle remark lines with color
                    if (typeof line === "object" && line.type === "remark") {
                      const { remark } = line;
                      return (
                        <p
                          key={index}
                          className={cn(
                            "font-bold leading-relaxed transition-opacity duration-1000 mb-[1vw] tracking-wide",
                            visibleLines > index ? "opacity-100" : "opacity-0",
                          )}
                          style={{
                            fontSize: "clamp(1rem, 3.5vw, 1.5rem)",
                          }}
                        >
                          <span
                            className="px-1.5 py-0.5 rounded"
                            style={{
                              backgroundColor: remark.color,
                              color: getContrastColor(remark.color),
                              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                            }}
                          >
                            {renderJitteryText(remark.colorName)}
                          </span>
                          <span className="text-stone-800">
                            {renderJitteryText("... ")}
                          </span>
                          <span className="text-stone-800">
                            {renderJitteryText(remark.statement)}
                          </span>
                        </p>
                      );
                    }

                    const isRating = displayPhase === "phase3" && index === 0;
                    const textLine = typeof line === "string" ? line : "";

                    return (
                      <p
                        key={index}
                        className={cn(
                          "text-stone-800 font-bold leading-relaxed transition-opacity duration-1000 mb-[1vw] tracking-wide",
                          visibleLines > index ? "opacity-100" : "opacity-0",
                          isRating ? "tracking-wider" : "",
                        )}
                        style={{
                          fontSize: isRating
                            ? "clamp(1.25rem, 4vw, 1.875rem)"
                            : "clamp(1rem, 3.5vw, 1.5rem)",
                        }}
                      >
                        {renderJitteryText(textLine)}
                      </p>
                    );
                  })}

                  {/* Pointer hint inside bubble */}
                  <div
                    className={cn(
                      "mt-4 transition-opacity duration-300",
                      displayPhase &&
                        phaseLines.length > 0 &&
                        visibleLines >= phaseLines.length
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  >
                    <Pointer className="w-[5vw] h-[5vw] max-w-7 max-h-7 min-w-5 min-h-5 text-stone-600 pointer-hint" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Helper to render jittery text
function renderJitteryText(text: string | undefined | null) {
  if (!text) return null;
  return (
    <span>
      {text.split("").map((char, i) => (
        <span
          key={i}
          className="jitter-letter"
          style={{
            animationDelay: `${i * 0.02}s`,
            animationDuration: `${2 + (i % 4) * 0.3}s`,
          }}
        >
          {char === " " ? "\u00A0" : char}
        </span>
      ))}
    </span>
  );
}
